import { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { useGetMeQuery } from './store/authApi';
import { setCredentials } from './store/authSlice';
import Home from './components/Home';
import Form from './components/Form';
import NotFound from './components/NotFound';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Verified from './components/Verified';
import PrivateRoute from './components/PrivateRoute';
import AppShell from './components/layout/AppShell';
import AdminDashboard from './components/AdminDashboard';
import UserList from './components/UserList';
import AdminList from './components/AdminList';
import FormList from './components/FormList';
import SprintHistory from './components/SprintHistory';
import SprintList from './components/SprintList';
import SprintForm from './components/SprintForm';
import MyPublishedBlogs from './components/MyPublishedBlogs';
import MyDrafts from './components/MyDrafts';
import PublishedBlogs from './components/PublishedBlogs';
import ContentCalendar from './components/ContentCalendar';
import BacklinksHub from './components/BacklinksHub';
import Invites from './components/Invites';
import AcceptInvite from './components/AcceptInvite';
import Profile from './components/Profile';
import './App.css';
import ViewBlog from './components/ViewBlog';
import Loader from './helpers/loader';

function App() {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state: RootState) => state.auth);

  const { data: fetchedUser, isSuccess, isLoading } = useGetMeQuery(undefined, {
    skip: !token || !!user,
  });

  useEffect(() => {
    if (isSuccess && fetchedUser && token) {
      dispatch(setCredentials({ user: fetchedUser, token }));
    }
  }, [fetchedUser, isSuccess, token, dispatch]);

  if (isLoading) return <Loader />;

  return (
    <Router>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3500,
          style: {
            background: '#fff',
            color: '#111827',
            border: '1px solid #F3F4F6',
            borderRadius: '12px',
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 500,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.08)',
          },
          success: { iconTheme: { primary: '#FF6B35', secondary: '#fff' } },
        }}
      />
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verified" element={<Verified />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Private: wrapped in AppShell (sidebar + topbar) */}
        <Route element={<PrivateRoute />}>
          <Route element={<AppShell />}>
            <Route path="/" element={<Home />} />
            <Route path="/my/published" element={<MyPublishedBlogs />} />
            <Route path="/my/drafts" element={<MyDrafts />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/form/:id" element={<Form />} />

            <Route path="/admin" element={<AdminDashboard />}>
              <Route index element={<SprintHistory />} />
              <Route path="sprint-actions" element={<SprintHistory />} />
              <Route path="sprints" element={<SprintList />} />
              <Route path="sprints/:id" element={<SprintForm />} />
              <Route path="user" element={<UserList />} />
              <Route path="admins" element={<AdminList />} />
              <Route path="invites" element={<Invites />} />
              <Route path="forms" element={<FormList />} />
              <Route path="published-blogs" element={<PublishedBlogs />} />
              <Route path="calendar" element={<ContentCalendar />} />
              <Route path="backlinks-hub" element={<BacklinksHub />} />
              <Route path="blog/:id" element={<ViewBlog />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
